/**
 * Utility functions for masking sensitive data in logs and outputs
 */

/**
 * Mask a database connection string
 *
 * @param connectionString Database connection string
 * @returns Masked connection string
 */
export function maskConnectionString(connectionString: string): string {
  if (!connectionString) {
    return '';
  }

  try {
    // Handle different connection string formats

    // PostgreSQL format: postgresql://username:password@host:port/database
    if (connectionString.includes('@') && connectionString.includes(':')) {
      return connectionString.replace(/\/\/(.*?):(.*?)@/, '//****:****@');
    }

    // MongoDB format: mongodb+srv://username:password@host/database
    if (connectionString.startsWith('mongodb') && connectionString.includes('@')) {
      return connectionString.replace(/(mongodb(\+srv)?:\/\/)(.*?):(.*)@/, '$1****:****@');
    }

    // JDBC format: jdbc:postgresql://host:port/database?user=username&password=password
    if (connectionString.includes('password=')) {
      return connectionString.replace(/password=([^&]*)/, 'password=****');
    }

    // If no specific format matches, mask any credential-like parts
    return connectionString.replace(/:(.*?)@/, ':****@');
  } catch (error) {
    // In case of any error, return a completely masked string
    return '****';
  }
}

/**
 * Mask a JWT or other authentication token
 *
 * @param token Authentication token
 * @returns Masked token
 */
export function maskAuthToken(token: string): string {
  if (!token) {
    return '';
  }

  try {
    // If it's a Bearer token, just keep the prefix
    if (token.startsWith('Bearer ')) {
      return 'Bearer ****';
    }

    // If it's a JWT (has two dots), mask all but first few and last few chars
    if (token.split('.').length === 3) {
      const parts = token.split('.');
      if (parts[0]) {
        return `${parts[0].substring(0, 3)}...${parts[0].slice(-3)}.****.**`;
      }
      return '****';
    }

    // Default masking for other token types
    if (token.length > 8) {
      return `${token.substring(0, 4)}...${token.slice(-4)}`;
    }

    return '****';
  } catch (error) {
    return '****';
  }
}

/**
 * Mask any sensitive data based on field name or content
 *
 * @param key Field key
 * @param value Field value
 * @returns Masked value if sensitive, original value otherwise
 */
export function maskSensitiveData(value: any, key?: string): any {
  // If value is null or undefined, return as is
  if (value === null || value === undefined) {
    return value;
  }

  // If it's not a string, check if it's an object
  if (typeof value !== 'string') {
    if (typeof value === 'object' && !Array.isArray(value)) {
      // Recursively mask sensitive data in objects
      const maskedObj: Record<string, any> = {};
      for (const objKey in value) {
        maskedObj[objKey] = maskSensitiveData(value[objKey], objKey);
      }
      return maskedObj;
    }

    // Return non-string, non-object values as is
    return value;
  }

  // List of sensitive field names (case-insensitive)
  const sensitiveKeys = [
    'password', 'secret', 'token', 'key', 'credential',
    'auth', 'apikey', 'api_key', 'private', 'passwd'
  ];

  // Check if the key indicates sensitive data
  if (key && sensitiveKeys.some(k => key.toLowerCase().includes(k))) {
    return '****';
  }

  // Check for connection strings
  if (value.includes('://') &&
    (value.includes('@') || value.includes('password='))) {
    return maskConnectionString(value);
  }

  // Check for JWT-like tokens
  if (key?.toLowerCase().includes('token') ||
    (value.split('.').length === 3 && value.length > 40)) {
    return maskAuthToken(value);
  }

  // Return the original value if not sensitive
  return value;
}

/**
 * Deep mask sensitive data in an object
 *
 * @param obj Object to mask
 * @returns New object with sensitive data masked
 */
export function maskSensitiveObject<T extends Record<string, any>>(obj: T): T {
  return maskSensitiveData(obj) as T;
}
